import { useCallback, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface BufferedRange { start: number; end: number }

interface Props {
  currentTime: number;
  duration: number;
  buffered: BufferedRange[];
  onSeek: (time: number) => void;
}

function pctOf(value: number, max: number): string {
  if (!Number.isFinite(max) || max <= 0) return '0%';
  return `${Math.max(0, Math.min(100, (value / max) * 100))}%`;
}

export function SeekBar({ currentTime, duration, buffered, onSeek }: Props): JSX.Element {
  const { t } = useTranslation();
  const railRef = useRef<HTMLDivElement>(null);
  const [scrubPct, setScrubPct] = useState<number | null>(null);
  const scrubbingRef = useRef(false);

  const timeFromEvent = useCallback((clientX: number): number => {
    const rail = railRef.current;
    if (!rail || !Number.isFinite(duration) || duration <= 0) return 0;
    const rect = rail.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  const onPointerDown = useCallback((e: PointerEvent<HTMLDivElement>): void => {
    scrubbingRef.current = true;
    const ratio = timeFromEvent(e.clientX) / Math.max(1, duration);
    setScrubPct(ratio * 100);
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  }, [timeFromEvent, duration]);

  const onPointerMove = useCallback((e: PointerEvent<HTMLDivElement>): void => {
    if (!scrubbingRef.current) return;
    const ratio = timeFromEvent(e.clientX) / Math.max(1, duration);
    setScrubPct(ratio * 100);
  }, [timeFromEvent, duration]);

  const onPointerUp = useCallback((e: PointerEvent<HTMLDivElement>): void => {
    if (!scrubbingRef.current) return;
    scrubbingRef.current = false;
    const t = timeFromEvent(e.clientX);
    setScrubPct(null);
    onSeek(t);
  }, [timeFromEvent, onSeek]);

  const onPointerCancel = useCallback((): void => {
    scrubbingRef.current = false;
    setScrubPct(null);
  }, []);

  return (
    <div
      ref={railRef}
      className="player-seekbar"
      data-testid="seek-rail"
      role="slider"
      aria-label={t('player.seek')}
      aria-valuemin={0}
      aria-valuemax={Math.max(0, Math.floor(duration))}
      aria-valuenow={Math.floor(currentTime)}
      // The scrub is a pointer-capture drag. On touch screens the browser
      // claims any drag for panning unless the element opts out, and then
      // fires `pointercancel` instead of pointermove/pointerup — iOS Safari
      // did exactly that, so the seek bar could not be dragged on iPhone.
      // Kept inline (not in the stylesheet) because the handlers below are
      // only correct together with it.
      style={{ touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {buffered.map((r) => (
        <div
          key={`${r.start}-${r.end}`}
          className="player-seekbar__buffered"
          style={{
            left: pctOf(r.start, duration),
            width: pctOf(r.end - r.start, duration),
          }}
        />
      ))}
      <div
        className="player-seekbar__playhead"
        data-testid="seek-playhead"
        style={{ ['--pct' as string]: pctOf(currentTime, duration) }}
      />
      {scrubPct != null && (
        <div
          className="player-seekbar__preview"
          data-testid="seek-preview"
          style={{ ['--pct' as string]: `${scrubPct}%` }}
        />
      )}
    </div>
  );
}
